/**
 * The cameras whose player session has an open data channel, as a set of owners for each
 * camera. An owner is one player (`usePlayerStreams`). A camera stays published until its last
 * owner closes it, because two players can show the same camera. The notifications panel reads
 * it to tell "open the camera" from "waiting for events".
 */

import type { Atom, PrimitiveAtom, WritableAtom } from 'jotai';
import { atom } from 'jotai';
import { atomFamily } from 'jotai-family';

/** One owner opens or closes a list of cameras. */
interface PublishersChange {
    owner: string;
    ids: readonly string[];
    isOpen: boolean;
}

/** Frozen, so every reader shares one identity for "no owner". */
const EMPTY: ReadonlySet<string> = Object.freeze(new Set<string>());

// Exported for the test only.
export const publisherOwnersAtom = atomFamily(
    (_cameraId: string): PrimitiveAtom<ReadonlySet<string>> =>
        atom<ReadonlySet<string>>(EMPTY));

/** Add or remove the owner of each camera. A write that changes nothing keeps the set. */
export const setPublishersAtom: WritableAtom<null, [PublishersChange], void> = atom(
    null,
    (get, set, { owner, ids, isOpen }: PublishersChange): void => {
        for (const id of ids) {
            const ownersAtom = publisherOwnersAtom(id);
            const owners = get(ownersAtom);
            if (owners.has(owner) === isOpen) {
                continue;
            }
            const nextIds = new Set(owners);
            if (isOpen) {
                nextIds.add(owner);
            } else {
                nextIds.delete(owner);
            }
            set(ownersAtom, nextIds.size === 0 ? EMPTY : nextIds);
        }
    },
);

/** Whether any player holds the camera's data channel open. */
export const cameraPublishedAtom = atomFamily((cameraId: string): Atom<boolean> =>
    atom((get): boolean => get(publisherOwnersAtom(cameraId)).size > 0));
